import { useEffect, useState } from 'react';

import {
  CapabilityClientError,
  type ColumnChoices,
  type ImportJob,
  type ImportRow,
  UNCATEGORIZED,
  canPublish,
  categoryOptions as treeOptions,
  categoryQuestion,
  categoryValues,
  importStage,
  initialChoices,
  mappingProblems,
  rowProblems,
  targetOptions,
  toMapping,
} from '@going-once/client';

import { api } from '../lib/api';
import { describeError, useAsync } from '../lib/useAsync';
import { Card, Field, Pressable, Select, Stack, Text } from '../ui/primitives';
import { ErrorNote } from '../ui/state';

/**
 * **The CSV import, bare — a harness for the team, not a seller surface** (D55; O32: no
 * seller web experience in V1). Demos, tests, and reproducing a bug with a desktop file.
 * No design and no parity with the phone, which is where sellers import: a file input,
 * the proposal as a table with a select per column, confirm, a progress line, publish.
 *
 * Same capabilities and the same rules (`packages/client/src/imports.ts`) as the phone, so
 * what works here works there. At `/sell/import`, linked from `/sell` **outside production
 * only** (the environment's `namespace` in `config.json`; PR #44 review), for a seller the
 * import is on for.
 */
export function ImportHarness() {
  const [jobId, setJobId] = useState<string>();
  const [problem, setProblem] = useState<{ message: string }>();
  const [duplicate, setDuplicate] = useState<{ jobId: string; etag: string }>();
  const [busy, setBusy] = useState(false);
  const recent = useAsync(() => api().query('catalog.import.list', {}), [jobId]);

  async function upload(file: File) {
    setProblem(undefined);
    setDuplicate(undefined);
    setBusy(true);
    try {
      const started = await api().command(
        'catalog.import.start',
        { fileName: file.name.slice(0, 200), sizeBytes: file.size },
        { idempotencyKey: crypto.randomUUID() },
      );
      const put = await fetch(started.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': started.contentType },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status}).`);
      const etag = put.headers.get('etag') ?? '';
      try {
        await api().command('catalog.import.begin', { jobId: started.jobId, ...(etag ? { etag } : {}) }, {});
        setJobId(started.jobId);
      } catch (error) {
        if (error instanceof CapabilityClientError && error.code === 'DUPLICATE_FILE') {
          setDuplicate({ jobId: started.jobId, etag });
        } else throw error;
      }
    } catch (error) {
      setProblem(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function importAnyway() {
    if (!duplicate) return;
    try {
      await api().command(
        'catalog.import.begin',
        { jobId: duplicate.jobId, etag: duplicate.etag, confirmDuplicate: true },
        {},
      );
      setJobId(duplicate.jobId);
      setDuplicate(undefined);
    } catch (error) {
      setProblem(describeError(error));
    }
  }

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text as="h2" step="heading">
          CSV import — team harness
        </Text>
        <Text step="label" tone="fg.muted">
          Not a seller surface. Sellers import on the phone.
        </Text>
      </Stack>

      <Card>
        <Stack gap="sm">
          <Text as="label" htmlFor="import-file" step="label">
            A CSV file (up to 10 MB, 10,000 rows)
          </Text>
          <input
            id="import-file"
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
              event.target.value = '';
            }}
          />
          {busy ? <Text tone="fg.muted">Uploading…</Text> : null}
          {duplicate ? (
            <Stack gap="sm" align="start">
              <Text tone="warning">This file was imported recently; its quantities would be added again.</Text>
              <Pressable variant="secondary" onPress={importAnyway}>
                Import anyway
              </Pressable>
            </Stack>
          ) : null}
          {problem ? <Text tone="danger">{problem.message}</Text> : null}
        </Stack>
      </Card>

      {recent.error ? <ErrorNote error={recent.error} onRetry={recent.reload} /> : null}
      {recent.data && recent.data.jobs.length > 0 ? (
        <Stack gap="xs">
          <Text step="label" tone="fg.muted">
            Recent imports
          </Text>
          {recent.data.jobs.map((job) => (
            <Pressable
              key={job.jobId}
              variant={job.jobId === jobId ? 'secondary' : 'quiet'}
              onPress={() => setJobId(job.jobId)}
            >
              {job.fileName} — {importStage(job).title}
            </Pressable>
          ))}
        </Stack>
      ) : null}

      {jobId ? <JobPanel key={jobId} jobId={jobId} /> : null}
    </Stack>
  );
}

function JobPanel({ jobId }: { jobId: string }) {
  const status = useAsync(() => api().query('catalog.import.status', { jobId, rows: 'problems', limit: 100 }), [jobId]);
  const job = status.data?.job;
  const running = job ? importStage(job).running : false;

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(status.reload, 3000);
    return () => clearInterval(timer);
  }, [running, status.reload]);

  if (status.error) return <ErrorNote error={status.error} onRetry={status.reload} />;
  if (!job) return <Text tone="fg.muted">Loading…</Text>;
  const stage = importStage(job);

  return (
    <Stack gap="md">
      <Text step="title">
        {stage.title}
        {stage.detail ? ` — ${stage.detail}` : ''}
      </Text>
      {job.verification && !job.verification.ok
        ? job.verification.errors.map((error, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a report's errors have no id and never reorder
            <Text key={index} tone="danger">
              {error.line ? `Line ${error.line}: ` : ''}
              {error.message}
            </Text>
          ))
        : null}
      {job.status === 'awaiting_confirmation' && job.proposal ? <Mapping job={job} onDone={status.reload} /> : null}
      {canPublish(job) ? <PublishButton job={job} onDone={status.reload} /> : null}
      <Problems rows={status.data?.rows?.items ?? []} />
    </Stack>
  );
}

function Mapping({ job, onDone }: { job: ImportJob; onDone: () => void }) {
  const proposal = job.proposal;
  const taxonomy = useAsync(() => api().query('catalog.taxonomy.get', {}), []);
  const [choices, setChoices] = useState<ColumnChoices>(() => (proposal ? initialChoices(proposal) : {}));
  const [fallback, setFallback] = useState<string>('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [remember, setRemember] = useState(false);
  const [problem, setProblem] = useState<{ message: string }>();
  const [categorySearch, setCategorySearch] = useState('');
  if (!proposal) return null;

  const attributes = taxonomy.data?.attributes ?? [];
  // The tree is Shopify's (D59): Going Once's own categories until a search, and the chosen one always.
  const categoryOptions = (selected?: string) => [
    { value: '', label: '—' },
    ...(taxonomy.data
      ? treeOptions(taxonomy.data, {
          search: categorySearch,
          ...(selected && selected !== UNCATEGORIZED ? { selected } : {}),
        })
      : []),
    { value: UNCATEGORIZED, label: 'Uncategorized' },
  ];
  const question = categoryQuestion(proposal, choices);
  const problems = mappingProblems(proposal, choices, { attributes, ...(fallback ? { fallback } : {}) });

  async function confirm() {
    setProblem(undefined);
    try {
      const matched = categoryValues(question, values);
      await api().command(
        'catalog.import.confirm',
        {
          jobId: job.jobId,
          mapping: toMapping(proposal as NonNullable<typeof proposal>, choices),
          categories: { ...(matched.length > 0 ? { values: matched } : {}), ...(fallback ? { fallback } : {}) },
          remember,
        },
        {},
      );
      onDone();
    } catch (error) {
      const details = error instanceof CapabilityClientError ? error.details : undefined;
      const listed = (details as { problems?: { message: string }[] } | undefined)?.problems;
      setProblem({ message: [describeError(error).message, ...(listed ?? []).map((p) => p.message)].join(' ') });
    }
  }

  return (
    <Card>
      <Stack gap="sm">
        <Text>
          {proposal.productCount} products detected
          {proposal.format ? ` · ${proposal.format} format` : ''}
        </Text>
        {proposal.columns.map((column) => (
          <Select
            key={column.index}
            id={`column-${column.index}`}
            label={`${column.header} (${column.confidence}%${column.needsReview ? ', review' : ''}) — e.g. ${column.samples.join(' | ')}`}
            value={choices[column.index] ?? 'skip'}
            onChange={(target) => setChoices((current) => ({ ...current, [column.index]: target }))}
            options={targetOptions(attributes)}
          />
        ))}
        {question.kind === 'whole-import' ? (
          <Select
            id="import-fallback"
            label="Category for the whole import"
            value={fallback}
            onChange={setFallback}
            options={categoryOptions(fallback)}
          />
        ) : null}
        {question.kind !== 'none' ? (
          <Field id="category-search" label="Find a category" value={categorySearch} onChange={setCategorySearch} />
        ) : null}
        {question.kind === 'values'
          ? question.values.map((entry) => (
              <Select
                key={entry.value}
                id={`value-${entry.value}`}
                label={`“${entry.value}” (${entry.count})${entry.flagged ? ' — no confident match' : ''}`}
                value={values[entry.value] ?? (entry.flagged ? '' : (entry.match?.categoryId ?? ''))}
                onChange={(category) => setValues((current) => ({ ...current, [entry.value]: category }))}
                options={categoryOptions(values[entry.value] ?? entry.match?.categoryId)}
              />
            ))
          : null}
        {proposal.notices.map((notice) => (
          <Text key={notice.code} step="label" tone="fg.muted">
            {notice.message}
          </Text>
        ))}
        <Text as="label" htmlFor="import-remember" step="label">
          <input
            id="import-remember"
            type="checkbox"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
          />{' '}
          Remember these choices
        </Text>
        {problems.map((entry) => (
          <Text key={entry} tone="warning">
            {entry}
          </Text>
        ))}
        {problem ? <Text tone="danger">{problem.message}</Text> : null}
        <Pressable onPress={confirm} disabled={problems.length > 0}>
          Confirm import
        </Pressable>
      </Stack>
    </Card>
  );
}

function PublishButton({ job, onDone }: { job: ImportJob; onDone: () => void }) {
  const [problem, setProblem] = useState<{ message: string }>();
  return (
    <Stack gap="xs" align="start">
      <Pressable
        onPress={() =>
          api()
            .command('catalog.import.publish', { jobId: job.jobId }, {})
            .then(onDone, (error: unknown) => setProblem(describeError(error)))
        }
      >
        {job.status === 'published' ? 'Publish again' : 'Publish drafts'}
      </Pressable>
      {problem ? <Text tone="danger">{problem.message}</Text> : null}
    </Stack>
  );
}

function Problems({ rows }: { rows: readonly ImportRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Stack gap="xs">
      <Text step="label" tone="fg.muted">
        Rows that need a look
      </Text>
      {rows.map((row) => (
        <Text key={row.line} step="label">
          Line {row.line} {row.title ? `(${row.title})` : ''}:{' '}
          {rowProblems(row)
            .map((entry) => entry.message)
            .join(' ')}
        </Text>
      ))}
    </Stack>
  );
}
